import { Box } from '@mui/material';
import { PaloBonusBadge, PaloBonusCondition } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { Api } from '../../../api';
import { AppState } from '../../../store/models/app-state';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/*
 * Asked once per supplier and page: an order lists the same supplier in many
 * rows, and the conditions do not change while one looks at them.
 */
const asked = new Map<number, Promise<PaloBonusCondition[]>>();

/**
 * The POOL bonus of a supplier, wherever a document names one — the badge
 * the offers and the supplier search show (#2256, #2259). It reads the
 * supplier's conditions itself, so a document need not load them into its
 * form: the order keeps its own copy of them as text, which loading would
 * overwrite.
 */
export const PaloSupplierBonusBadge = ({
    supplierId,
    supplierName,
    at,
}: {
    supplierId?: number | null;
    supplierName?: string;
    at?: Date | string | null;
}): ReactElement => {
    const dispatch = useThunkDispatch();
    const [conditions, setConditions] = useState<PaloBonusCondition[] | undefined>(undefined);

    useEffect(() => {
        let current = true;

        if (!supplierId) {
            setConditions([]);
            return;
        }

        if (!asked.has(supplierId)) {
            asked.set(
                supplierId,
                dispatch(async (_dispatch, _getState: () => AppState, { api }: { api: Api }) => {
                    const response = await api.accounts.getBonusConditions(supplierId);

                    return response.success ? response.result.bonus ?? [] : [];
                })
            );
        }

        void asked.get(supplierId)?.then(result => current && setConditions(result));

        return () => {
            current = false;
        };
    }, [dispatch, supplierId]);

    if (undefined === conditions) {
        return (
            <Box component="span" sx={{ color: tokens.color.ink3 }}>
                …
            </Box>
        );
    }

    return <PaloBonusBadge conditions={conditions} at={at} supplierName={supplierName} />;
};
